(ns arthur.events.collab
  "Everything that makes a document somewhere other people are: its address, who
  you are, who else is in it, and their writes arriving while you work.

  docs/architecture.md, Collaboration, and tl's model with the four additions it
  asks for. Writes stay on HTTP; the socket carries presence and the deltas the
  server broadcasts after a write commits.

  ONE RULE FOR THE ADDRESS AND THE ROOM. They follow `[:project :id]`, whatever
  event changed it — open, save, new, a copy — through one interceptor, so no
  event that loads a document has to remember to join its room.

  THE OUTBOX RULE, without an outbox. A remote leaf lands unless we have a change
  to that leaf the server has not seen — a leaf whose local value differs from the
  last value we synced. Otherwise their write would snap our unsaved edit back.
  The next save sends ours, and if theirs moved since, it answers 409 and we catch
  up, and the save after that is ours."
  (:require [arthur.domain.leaf :as leaf]
            [arthur.domain.project :as project]
            [arthur.events.edit :as edit]
            [arthur.events.playback :as pb]
            [arthur.events.project :as events.project]
            [arthur.footage.store :as store]
            [arthur.fx.http :as http]
            [clojure.string :as str]
            [re-frame.core :as rf]))

;; ---------------------------------------------------------------------------
;; the address

(defn- path-id
  "The project a path names: `/p/<uuid>/<slug>`. The slug is for people; the
  id is what finds it."
  [path]
  (second (re-matches #"/p/([0-9a-fA-F-]{36})(?:/.*)?" path)))

(defn slug [name]
  (or (not-empty (-> (str/lower-case (or name ""))
                     (str/replace #"[^a-z0-9]+" "-")
                     (str/replace #"^-+|-+$" "")))
      "untitled"))

(defn project-path [id name] (str "/p/" id "/" (slug name)))

(defn- route! []
  (rf/dispatch [::routed (path-id (.. js/window -location -pathname))]))

(defn navigate! [path]
  (.pushState js/history nil "" path)
  (route!))

(rf/reg-event-fx
 ::routed
 ;; `/` is the index of your projects; a project is only ever at its address.
 (fn [{:keys [db]} [_ id]]
   (cond
     (nil? id) {:db (assoc db :route :index)
                :dispatch [::events.project/list]}
     (= id (get-in db [:project :id])) {:db (assoc db :route [:project id])}
     :else {:db (assoc db :route [:project id])
            :dispatch [::events.project/open id]})))

(rf/reg-sub ::route (fn [db _] (:route db)))

(rf/reg-fx
 ::create!
 (fn [settings]
   (-> (http/POST "/api/projects" #js {:name (:name settings)})
       (.then (fn [^js made] (rf/dispatch [::created (.-id made) (.-name made) settings])))
       (.catch #(rf/dispatch [::refused (ex-message %)])))))

(rf/reg-fx ::navigate! navigate!)

;; `settings` is what `clip/blank` takes. The server holds only the name until
;; the first save, so the rest waits under the new id for the first open.
(rf/reg-event-fx
 ::create
 (fn [_ [_ settings]]
   {::create! (update settings :name #(or (not-empty %) "untitled"))}))

(rf/reg-event-fx
 ::created
 (fn [{:keys [db]} [_ id name settings]]
   {:db (assoc-in db [:new-projects id] settings)
    ::navigate! (project-path id name)}))

;; ---------------------------------------------------------------------------
;; the socket

(defonce ^:private socket (atom nil))
(defonce ^:private conn (atom {:id nil :tries 0 :timer nil}))

(defn- ws-url [id]
  (str (if (= "https:" (.. js/window -location -protocol)) "wss://" "ws://")
       (.. js/window -location -host) "/ws/projects/" id))

(declare open!)

(defn- retry-later! [id]
  (let [tries (:tries @conn)
        delay (min 30000 (* 500 (js/Math.pow 2 tries)))]
    (swap! conn assoc :tries (inc tries)
           :timer (js/setTimeout #(when (= id (:id @conn)) (open! id)) delay))))

(defn- open! [id]
  (let [s (js/WebSocket. (ws-url id))]
    (reset! socket s)
    (set! (.-onopen s) (fn [_] (swap! conn assoc :tries 0)))
    (set! (.-onmessage s) (fn [e] (rf/dispatch [::message (js/JSON.parse (.-data e))])))
    ;; Only the CURRENT socket clears the roster and retries: closing the last
    ;; project's on a switch must not wipe the new one's.
    (set! (.-onclose s) (fn [_]
                          (when (identical? s @socket)
                            (reset! socket nil)
                            (rf/dispatch [::peers-reset])
                            (retry-later! id))))))

(defn- connect! [id]
  (some-> (:timer @conn) js/clearTimeout)
  (when-let [s @socket] (set! (.-onclose s) nil) (.close s))
  (reset! socket nil)
  (reset! conn {:id id :tries 0 :timer nil})
  ;; Another room: whoever we were following is not in it.
  (rf/dispatch [::peers-reset :elsewhere])
  (when id (open! id)))

(defn send!
  "Say `msg` to the room, if we are in it; otherwise it is dropped."
  [msg]
  (when-let [^js s @socket]
    (when (= 1 (.-readyState s))               ; OPEN
      (.send s (js/JSON.stringify (clj->js msg))))))

(rf/reg-fx ::send! send!)

(rf/reg-fx
 ::follow!
 (fn [{:keys [id name]}]
   (when id
     (let [here (.. js/window -location -pathname)
           path (project-path id name)]
       (cond
         (= path here) nil
         ;; Renamed: the same page, a new slug, and no new history entry.
         (= id (path-id here)) (.replaceState js/history nil "" path)
         :else (.pushState js/history nil "" path))))
   (set! (.-title js/document) (if name (str name " — arthur") "arthur"))
   (when (not= id (:id @conn))
     (connect! id))))

(rf/reg-fx ::reconnect! (fn [_] (connect! (:id @conn))))

(def autosave?
  "Every edit saves. Off only for tests that need an edit held unsaved."
  true)

(def ^:private follow
  "The address, the title and the room follow the open project, and the
  document saves itself on every edit — `:paint/revision` is what moves when
  the document does. A save with nothing to send sends nothing, and one made
  while another is in flight goes when it lands.

  THERE IS NO BARE PROJECT. A document with no id on screen at a project's
  address — a built-in example, opened from the menu — is saved at once, and
  becomes a project with an address of its own."
  (rf/->interceptor
   :id ::follow
   :after (fn [ctx]
            (let [db     (get-in ctx [:effects :db] (get-in ctx [:coeffects :db]))
                  before (get-in ctx [:coeffects :db :project])
                  after  (:project db)]
              (cond-> ctx
                (not= (select-keys before [:id :name]) (select-keys after [:id :name]))
                (update-in [:effects :fx] (fnil conj [])
                           [::follow! (select-keys after [:id :name])])

                (and autosave? (:id after) (vector? (:route db))
                     (not= (:paint/revision db) (get-in ctx [:coeffects :db :paint/revision])))
                (update-in [:effects :fx] (fnil conj [])
                           [:dispatch [::events.project/save {:auto? true}]])

                (and (nil? (:id after)) (vector? (:route db))
                     (or (:id before) (not= (:cid before) (:cid after))))
                (update-in [:effects :fx] (fnil conj [])
                           [:dispatch [::events.project/save]]))))))

;; ---------------------------------------------------------------------------
;; presence
;;
;; `:peers` is the roster, us included, as the room gossips it: cid → `{:cid
;; :user}` and whatever else each of us says about ourselves. `:presence` is
;; what WE say, and it is said again on every welcome, so a dropped socket comes
;; back as it left. Nothing here reads past `:cid` and `:user`: what the rest
;; means belongs to whoever `listen!`s.

(defonce ^:private listeners (atom #{}))

(defn listen!
  "Hear the room: every message it sends, as `[event-id message]` once the roster
  has it, and `#js {:kind \"reset\" :elsewhere bool}` when the room is lost."
  [event-id]
  (swap! listeners conj event-id))

(defn- heard [fx ^js m]
  (update fx :fx (fnil into []) (map (fn [l] [:dispatch [l m]])) @listeners))

(defn say
  "`db` with `fields` part of what we say about ourselves, and the effects that
  say it. `once` rides along on this message only."
  ([db fields] (say db fields nil))
  ([db fields once]
   (let [db (cond-> (update db :presence merge fields)
              (:peer-cid db) (update-in [:peers (:peer-cid db)] merge fields))]
     {:db db ::send! (merge {:kind "state"} (:presence db) once)})))

(defn colour
  "A collaborator's colour, the same on their face and anywhere else they show,
  and the same in every browser that sees them: it is read off their cid."
  [cid]
  (str "hsl(" (mod (reduce #(+ (* 31 %1) (.charCodeAt %2 0)) 0 cid) 360) " 60% 42%)"))

(defn- upsert
  "A peer's message laid over what we knew of them: one that does not mention a
  field must not take it away."
  [db ^js m]
  (update-in db [:peers (.-cid m)] merge
             (dissoc (js->clj m :keywordize-keys true) :kind)))

(rf/reg-event-fx
 ::peers-reset
 ;; Off the wire, everyone we could see is a guess. `elsewhere?` is another room.
 (fn [{:keys [db]} [_ elsewhere?]]
   (heard {:db (assoc db :peers {})} #js {:kind "reset" :elsewhere elsewhere?})))

(rf/reg-event-fx
 ::message
 (fn [{:keys [db]} [_ ^js m]]
   (heard
    (case (.-kind m)
      "welcome" (let [cid (.-cid m)]
                  {:db (assoc db :peer-cid cid
                              :peers {cid (merge {:cid cid :user (.-user m)} (:presence db))})
                   ::send! (merge {:kind "state"} (:presence db))
                   ;; Anything written between our GET and our joining the room
                   ;; was broadcast to a room we were not in yet.
                   :dispatch [::catch-up]})
      "roster"  {:db (reduce upsert db (array-seq (.-peers m)))}
      ("join" "state") {:db (upsert db m)}
      "leave"   {:db (update db :peers dissoc (.-cid m))}
      "delta"   {:dispatch [::delta m]}
      "access"  {:dispatch [::catch-up]}
      {})
    m)))

(rf/reg-sub
 ::peers
 ;; Everyone else in the room, by name.
 (fn [db _]
   (->> (vals (:peers db))
        (remove #(= (:cid %) (:peer-cid db)))
        (sort-by (juxt (comp nil? :user) :user :cid)))))

;; ---------------------------------------------------------------------------
;; their writes

(defn- put [m path v] (if (nil? v) (dissoc m path) (assoc m path v)))

(defn- landed
  "Their change laid over ours, as `[local synced behind]`; a nil value is a
  removal.

  A leaf we have changed and not saved keeps our value, and theirs waits in
  `behind` rather than in `synced`: `synced` is what we have SEEN, and putting
  theirs there would let our next save overwrite it without a word. `take?` is
  the first write winning — theirs was, so it goes on screen over ours."
  [local synced behind theirs take?]
  (let [pending? #(not= (get local %) (get synced %))]
    (reduce-kv (fn [[now seen behind] path v]
                 (cond
                   (not (pending? path)) [(put now path v) (put seen path v) behind]
                   take?                 [(put now path v) (put seen path v) (dissoc behind path)]
                   :else                 [now seen (assoc behind path v)]))
               [local synced behind] theirs)))

(rf/reg-fx
 ::fetch-blocks!
 (fn [{:keys [keys then]}]
   (-> (js/Promise.all (into-array (map #(http/GET (str "/api/blocks/" %)) keys)))
       (.then #(rf/dispatch (conj then (project/store %))))
       (.catch #(rf/dispatch [::events.project/failed (ex-message %)])))))

(rf/reg-event-fx
 ::remote
 ;; `written` and `removed` against what we last synced; `blocks` is the store
 ;; of any the new leaves name that we do not hold, once fetched.
 (fn [{:keys [db]} [_ {:keys [by written removed take?] at :seq :as change} blocks]]
   (let [cid    (get-in db [:project :cid])
         entry  (store/entry (:clip/current db))
         local  (leaf/leaves cid (:clip entry))
         theirs (merge written (zipmap removed (repeat nil)))
         lost   (if take?
                  (count (filter #(not= (get local %) (get (:synced entry) %)) (keys theirs)))
                  0)
         [now synced behind] (landed local (:synced entry) (:behind entry) theirs take?)
         have   (merge (:store entry) blocks)
         lack   (remove #(contains? have %) (project/block-keys now))
         status (fn [now synced]
                  (if (pos? lost)
                    (str lost (if (= 1 lost) " change" " changes")
                         " of yours lost to someone else's at the same moment — in your undo list")
                    (str (or by "someone") " saved r" at
                         (when (not= now synced) " · yours unsaved"))))]
     (cond
       (seq lack)
       {::fetch-blocks! {:keys lack :then [::remote change]}}

       (= now local)
       {:db (-> db
                (update :clip/current
                        #(or (store/edit-entry! % (fn [e] (assoc e :synced synced
                                                                 :behind behind)))
                             %))
                (assoc-in [:project :seq] at)
                ;; Our own write, back from the room, changes nothing to say.
                (cond-> (or take? (not= by (get-in db [:me :username])))
                  (assoc-in [:project :status] (status now synced))))}

       :else
       (let [clip (leaf/clip cid now)
             ;; Theirs, so not a step of ours to undo.
             db'  (-> (edit/replace-entry db #(-> %
                                                  (assoc :clip clip :synced synced
                                                         :behind behind)
                                                  (update :store merge blocks)))
                      (edit/transport clip)
                      (update :project merge
                              {:seq at :status (status now synced)}))]
         (cond-> {:db db'}
           (not= (:fps clip) (get-in db [:clip :fps]))
           (assoc ::pb/seek! [(:fps clip) (pb/frames db') (get-in db [:playback :frame])])))))))

(defn- ours
  "The clip in a delta or a document that is the one open here."
  [db clips]
  (let [cid (get-in db [:project :cid])]
    (first (filter #(= cid (.-cid ^js %)) (array-seq clips)))))

(rf/reg-event-fx
 ::delta
 (fn [{:keys [db]} [_ ^js m]]
   (let [local (get-in db [:project :seq])
         seq   (.-seq m)]
     (cond
       (or (nil? local) (<= seq local)) {}
       ;; A missed delta is a stale document forever, unless it is noticed.
       (> seq (inc local)) {:dispatch [::catch-up]}
       :else
       (let [^js c (ours db (.-clips m))]
         (cond-> {:db (cond-> (assoc-in db [:project :seq] seq)
                        (.-name m) (assoc-in [:project :name] (.-name m)))}
           c (assoc :dispatch [::remote {:seq seq :by (.-by m)
                                         :written (project/tier1 (.-leaves c))
                                         :removed (vec (.-removed c))}])))))))

(rf/reg-fx
 ::catch-up!
 (fn [[id take?]]
   (-> (http/GET (str "/api/projects/" id))
       (.then #(rf/dispatch [::caught-up % take?]))
       (.catch #(js/console.warn "catching up failed" %)))))

(rf/reg-event-fx
 ::catch-up
 (fn [{:keys [db]} [_ take?]]
   (if-let [id (get-in db [:project :id])]
     {::catch-up! [id take?]}
     {})))

(rf/reg-event-fx
 ::caught-up
 ;; The whole document, diffed against what we last synced: which leaves they
 ;; wrote, and which they deleted.
 (fn [{:keys [db]} [_ ^js loaded take?]]
   (let [^js c   (ours db (.-clips loaded))
         synced  (:synced (store/entry (:clip/current db)))
         theirs  (when c (project/tier1 (.-leaves c)))
         access  {:owner (.-owner loaded) :editors (vec (.-editors loaded))
                  :can-edit? (.-can_edit loaded)}]
     (cond-> {:db (update db :project merge access)}
       (and c (or take? (not= (.-seq loaded) (get-in db [:project :seq]))))
       (assoc :dispatch [::remote {:seq (.-seq loaded) :by nil :take? take?
                                   :written (into {} (remove (fn [[p v]] (= v (get synced p))))
                                                  theirs)
                                   :removed (remove #(contains? theirs %) (keys synced))}])))))

;; ---------------------------------------------------------------------------
;; who you are, and who else may write

(rf/reg-fx
 ::request!
 (fn [{:keys [method url body then]}]
   (-> (http/request! method url body)
       (.then #(rf/dispatch (conj then %)))
       (.catch #(rf/dispatch [::refused (ex-message %)])))))

(rf/reg-event-fx ::who (fn [_ _] {::request! {:method "GET" :url "/api/me" :then [::signed]}}))

(rf/reg-event-fx
 ::sign-in
 (fn [_ [_ mode username password]]
   {::request! {:method "POST" :url (str "/api/" (name mode))
                :body #js {:username username :password password}
                :then [::signed]}}))

(rf/reg-event-fx
 ::sign-out
 (fn [_ _] {::request! {:method "POST" :url "/api/logout" :then [::signed]}}))

(rf/reg-event-fx
 ::signed
 ;; Who you are changes what you may write and what the room calls you.
 (fn [{:keys [db]} [_ ^js who]]
   (let [username (.-username who)
         changed? (not= username (get-in db [:me :username]))]
     (cond-> {:db (assoc db :me {:username username})}
       (and changed? (contains? db :me)) (assoc ::reconnect! nil
                                                :fx [[:dispatch [::catch-up]]
                                                     [:dispatch [::events.project/list]]])))))

(rf/reg-event-db ::refused (fn [db [_ message]] (assoc-in db [:me :error] message)))

(rf/reg-sub ::me (fn [db _] (:me db)))

(rf/reg-event-fx
 ::add-editor
 (fn [{:keys [db]} [_ username]]
   {::request! {:method "POST" :url (str "/api/projects/" (get-in db [:project :id]) "/editors")
                :body #js {:username username} :then [::editors]}}))

(rf/reg-event-fx
 ::remove-editor
 (fn [{:keys [db]} [_ username]]
   {::request! {:method "DELETE"
                :url (str "/api/projects/" (get-in db [:project :id]) "/editors/"
                          (js/encodeURIComponent username))
                :then [::editors]}}))

(rf/reg-event-db
 ::editors
 (fn [db [_ ^js answer]]
   (-> (assoc-in db [:project :editors] (vec (.-editors answer)))
       (update :me dissoc :error))))

;; ---------------------------------------------------------------------------
;; snapshots: named versions, now that every edit saves itself

(defn- snapshots-url [db] (str "/api/projects/" (get-in db [:project :id]) "/revisions"))

(rf/reg-event-fx
 ::snapshots
 (fn [{:keys [db]} _]
   {::request! {:method "GET" :url (snapshots-url db) :then [::snapshots-listed]}}))

(rf/reg-event-db
 ::snapshots-listed
 (fn [db [_ ^js answer]]
   (assoc db :snapshots
          (mapv (fn [^js r] {:id (.-id r) :name (.-summary r) :author (.-author r)
                             :seq (.-seq r) :created (.-created r)})
                (array-seq (.-revisions answer))))))

(rf/reg-sub ::snapshot-list (fn [db _] (:snapshots db)))

(rf/reg-event-fx
 ::snapshot
 (fn [{:keys [db]} [_ name]]
   {::request! {:method "POST" :url (snapshots-url db) :body #js {:summary name}
                :then [::snapshotted name]}}))

(rf/reg-event-fx
 ::snapshotted
 (fn [{:keys [db]} [_ name _]]
   {:db (assoc-in db [:project :status] (str "snapshot \"" name "\" taken"))
    :dispatch [::snapshots]}))

(rf/reg-event-fx
 ::restore
 ;; An ordinary write on the server, which comes back to every open tab —
 ;; this one included — as a delta.
 (fn [{:keys [db]} [_ {:keys [id name]}]]
   {::request! {:method "POST" :url (str (snapshots-url db) "/" id "/restore")
                :then [::restored name]}}))

(rf/reg-event-db
 ::restored
 (fn [db [_ name _]] (assoc-in db [:project :status] (str "restored \"" name "\""))))

;; ---------------------------------------------------------------------------

(defn start!
  "Follow the open project from now on, show what the address names — the
  index, or a project — and answer the back button."
  []
  (rf/reg-global-interceptor follow)
  (rf/dispatch [::who])
  (route!)
  (.addEventListener js/window "popstate" route!))
